/**
 * ItemFilters - Filtering controls for item reservations
 */

import React from 'react';
import { XMarkIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';

const ItemFilters = ({ filters, onFiltersChange, onClose }) => {
  
  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters };
    
    if (key.includes('.')) {
      // Handle nested properties like dateRange.startDate
      const [parent, child] = key.split('.');
      newFilters[parent] = { ...newFilters[parent], [child]: value };
    } else {
      newFilters[key] = value;
    }
    
    onFiltersChange(newFilters);
  };

  const clearFilters = () => {
    onFiltersChange({
      status: 'all',
      rentalStatus: 'all',
      dateRange: { startDate: '', endDate: '' },
      searchTerm: '',
      customerFilter: '',
      serviceFilter: ''
    });
  };

  const hasActiveFilters = () => {
    return (
      filters.status !== 'all' ||
      filters.rentalStatus !== 'all' ||
      filters.dateRange?.startDate ||
      filters.dateRange?.endDate ||
      filters.searchTerm ||
      filters.customerFilter ||
      filters.serviceFilter
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900">Filter Reservations</h3>
        <div className="flex items-center space-x-2">
          {hasActiveFilters() && (
            <button
              onClick={clearFilters}
              className="text-sm text-gray-600 hover:text-gray-900 underline"
            >
              Clear All
            </button>
          )}
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Status Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Status
          </label>
          <select
            value={filters.status || 'all'}
            onChange={(e) => handleFilterChange('status', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">All Statuses</option>
            <option value="ACTIVE">Active</option>
            <option value="COMPLETED">Completed</option>
            <option value="CANCELLED">Cancelled</option>
            <option value="PENDING">Pending</option>
          </select>
        </div>

        {/* Rental Status Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Rental Alert
          </label>
          <select
            value={filters.rentalStatus || 'all'}
            onChange={(e) => handleFilterChange('rentalStatus', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">All</option>
            <option value="WARNING">⚠️ Approaching Limit</option>
            <option value="OVERDUE">🔴 Overdue</option>
          </select>
        </div>

        {/* Search Term */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Search
          </label>
          <div className="relative">
            <input
              type="text"
              placeholder="Customer name, phone, item..."
              value={filters.searchTerm || ''}
              onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:ring-blue-500 focus:border-blue-500"
            />
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-2.5" />
          </div>
        </div>

        {/* Customer Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Customer Name
          </label>
          <input
            type="text"
            placeholder="Filter by customer..."
            value={filters.customerFilter || ''}
            onChange={(e) => handleFilterChange('customerFilter', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        {/* Service Filter - Empty for Items but keeping consistent structure */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Item Type
          </label>
          <input
            type="text"
            placeholder="Filter by item type..."
            value={filters.itemTypeFilter || ''}
            onChange={(e) => handleFilterChange('itemTypeFilter', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      </div>

      {/* Date Range */}
      <div className="border-t border-gray-200 pt-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Date Range
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Start Date</label>
            <input
              type="date"
              value={filters.dateRange?.startDate || ''}
              onChange={(e) => handleFilterChange('dateRange.startDate', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">End Date</label>
            <input
              type="date"
              value={filters.dateRange?.endDate || ''}
              onChange={(e) => handleFilterChange('dateRange.endDate', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Active Filters Summary */}
      {hasActiveFilters() && (
        <div className="border-t border-gray-200 pt-4">
          <div className="text-sm text-gray-600 mb-2">Active Filters:</div>
          <div className="flex flex-wrap gap-2">
            {filters.status && filters.status !== 'all' && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                Status: {filters.status}
                <button
                  onClick={() => handleFilterChange('status', 'all')}
                  className="ml-1.5 inline-flex items-center justify-center w-4 h-4 rounded-full text-blue-400 hover:text-blue-600"
                >
                  ×
                </button>
              </span>
            )}

            {filters.rentalStatus && filters.rentalStatus !== 'all' && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                Alert: {filters.rentalStatus === 'WARNING' ? '⚠️ Approaching' : '🔴 Overdue'}
                <button
                  onClick={() => handleFilterChange('rentalStatus', 'all')}
                  className="ml-1.5 inline-flex items-center justify-center w-4 h-4 rounded-full text-orange-400 hover:text-orange-600"
                >
                  ×
                </button>
              </span>
            )}

            {filters.searchTerm && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                Search: {filters.searchTerm}
                <button
                  onClick={() => handleFilterChange('searchTerm', '')}
                  className="ml-1.5 inline-flex items-center justify-center w-4 h-4 rounded-full text-green-400 hover:text-green-600"
                >
                  ×
                </button>
              </span>
            )}
            
            {filters.customerFilter && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                Customer: {filters.customerFilter}
                <button
                  onClick={() => handleFilterChange('customerFilter', '')}
                  className="ml-1.5 inline-flex items-center justify-center w-4 h-4 rounded-full text-purple-400 hover:text-purple-600"
                >
                  ×
                </button>
              </span>
            )}
            
            {(filters.dateRange?.startDate || filters.dateRange?.endDate) && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                Date Range
                <button
                  onClick={() => handleFilterChange('dateRange', { startDate: '', endDate: '' })}
                  className="ml-1.5 inline-flex items-center justify-center w-4 h-4 rounded-full text-yellow-400 hover:text-yellow-600"
                >
                  ×
                </button>
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ItemFilters;